import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';

interface Feature { image: string; title: string; description: string; }
interface Step { number: number; image: string; title: string; description: string; }
interface Testimonial { name: string; quote: string; avatar: string; }
interface CaptureTag { label: string; icon: string; }
interface Faq { id: string; question: string; answer: string; }

@Component({
  selector: 'app-home',
  templateUrl: './home.html',
  styleUrl: './home.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink],
})
export class Home implements OnInit {
  private title = inject(Title);
  private meta = inject(Meta);

  activeFaq = signal<string | null>(null);

  readonly features: Feature[] = [
    { image: 'images/homepage/preserve-img1.png', title: 'fm-Guided Storytelling', description: 'Thoughtful prompts help unlock memories you didn\'t know you could share' },
    { image: 'images/homepage/preserve-img2.png', title: 'Family Collaboration', description: 'Invite loved ones to contribute stories and preserve your collective history' },
    { image: 'images/homepage/preserve-img3.png', title: 'Privacy-First', description: 'Your memories are encrypted and secured. You control who sees what, always' },
    { image: 'images/homepage/preserve-img4.png', title: 'Legacy QR Codes', description: 'Create memorial codes that let future generations contribute their own memories' },
  ];

  readonly steps: Step[] = [
    { number: 1, image: 'images/homepage/steps-info-1.png', title: 'Answer a Thoughtful Prompt', description: 'Control who sees what. Export to storybooks. Create legacy QR codes for memorials' },
    { number: 2, image: 'images/homepage/steps-info-2.png', title: 'Record Your Story', description: 'Control who sees what. Export to storybooks. Create legacy QR codes for memorials' },
    { number: 3, image: 'images/homepage/steps-info-3.png', title: 'Review & Organize', description: 'Control who sees what. Export to storybooks. Create legacy QR codes for memorials' },
    { number: 4, image: 'images/homepage/steps-info-4.png', title: 'Share & Preserve', description: 'Control who sees what. Export to storybooks. Create legacy QR codes for memorials' },
  ];

  readonly testimonials: Testimonial[] = [
    { name: 'Anita S.', quote: 'Having control over who can see my stories made me feel comfortable sharing openly. It\'s private, secure, and very personal.', avatar: 'images/homepage/avatar-1.png' },
    { name: 'Robert K.', quote: 'Having control over who can see my stories made me feel comfortable sharing openly. It\'s private, secure, and very personal.', avatar: 'images/homepage/avatar-2.png' },
    { name: 'Margaret L., Grandmother of 5', quote: 'Having control over who can see my stories made me feel comfortable sharing openly. It\'s private, secure, and very personal.', avatar: 'images/homepage/avatar-3.png' },
    { name: 'James T.', quote: 'Having control over who can see my stories made me feel comfortable sharing openly. It\'s private, secure, and very personal.', avatar: 'images/homepage/avatar-4.png' },
  ];

  readonly captureTags: CaptureTag[] = [
    { label: 'Childhood Stories', icon: 'images/icons/fm-childhood.svg' },
    { label: 'Family Traditions', icon: 'images/icons/fm-tf.svg' },
    { label: 'Love & Relationship', icon: 'images/icons/fm-lr.svg' },
    { label: 'Career & work', icon: 'images/icons/fm-cw.svg' },
    { label: 'Proudest Moments', icon: 'images/icons/fm-pm.svg' },
    { label: 'Grandparents & Heritage', icon: 'images/icons/fm-gh.svg' },
    { label: 'Dreams & Aspirations', icon: 'images/icons/fm-da.svg' },
    { label: 'Milestones', icon: 'images/icons/fm-milestones.svg' },
    { label: 'More...', icon: 'images/icons/fm-plus.svg' },
  ];

  readonly faqs: Faq[] = [
    { id: 'fmCollapseOne',   question: 'How does this platform work?',                  answer: 'Simply sign up, choose a category, answer guided prompts, record your story via video or audio, then share it securely with your family.' },
    { id: 'fmCollapseTwo',   question: 'What kind of memories can I record?',             answer: 'You can record anything â€” childhood memories, career milestones, family traditions, love stories, wisdom, and more.' },
    { id: 'fmCollapseThree', question: 'Are my memories safe and private?',               answer: 'Yes. All stories and media are encrypted and stored securely. Only you and those you invite can access your memories.' },
    { id: 'fmCollapseFour',  question: 'What are legacy QR codes?',                       answer: 'Legacy QR codes are physical or digital codes you can place on memorials or keepsakes. Scanning them lets future generations contribute their own memories.' },
    { id: 'fmCollapseFive',  question: 'Do I need to be tech-savvy to use it?',           answer: 'Not at all. The platform is designed to be simple and intuitive â€” if you can record a video on your phone, you can use Forever Memories.' },
    { id: 'fmCollapseSix',   question: 'Can I share my stories with family?',             answer: 'Absolutely. Create a Family Space and invite loved ones to contribute their own stories or view yours.' },
    { id: 'fmCollapseSeven', question: 'Can I edit or update my stories later?',          answer: 'Yes. You can revisit, edit, and add to your stories at any time. Your legacy is always a work in progress.' },
    { id: 'fmCollapseEight', question: 'Is there a limit to how many stories I can create?', answer: 'Free accounts can create a set number of stories. Premium plans unlock unlimited story creation, more storage, and additional features.' },
  ];

  ngOnInit(): void {
    this.title.setTitle('Forever Memories - Preserve Life Stories, Share Them Forever');
    this.meta.updateTag({ name: 'description', content: 'Capture the wisdom, love, and legacy of your life through video, audio, and photos. Forever Memories preserves your stories for generations.' });
    this.meta.updateTag({ property: 'og:title', content: 'Forever Memories' });
  }

  toggleFaq(id: string): void {
    this.activeFaq.update(current => current === id ? null : id);
  }
}


